/**
 * section-tracking.js
 * Fenge Section Level Analytics - Phase 1 Core Module
 *
 * 负责追踪:
 *   - section_view : Section 进入视口 50% 并停留 0.8s
 *   - section_click: Section 内的链接/按钮点击
 *   - scroll_depth : 滚动到新 Section 时记录深度
 *   - expand       : FAQ / 折叠内容展开
 *
 * 原理: 在 Theme (主线程) 采集数据 → analytics.publish() → Custom Pixel 沙盒 → GA4
 * 
 * @version 1.0.0
 */

(function () {
  'use strict';

  // ─── 工具函数 ────────────────────────────────────────────────────────────────

  /**
   * 向 Shopify Custom Pixel 沙盒发布自定义事件
   * @param {string} eventName  - 事件名称 (snake_case)
   * @param {object} eventData  - 事件参数
   */
  function publishEvent(eventName, eventData) {
    if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') {
      return;
    }
    try {
      window.Shopify.analytics.publish(eventName, eventData);
    } catch (err) {
      // 生产环境静默失败，不打断用户体验
    }
  }

  /**
   * 获取 Section 的 ID（从 Shopify section wrapper 的 id 属性）
   * @param {Element} el
   * @returns {string}
   */
  function getSectionId(el) {
    const wrapper = el.closest('[id^="shopify-section-"]');
    if (wrapper) {
      return wrapper.id.replace('shopify-section-', '');
    }
    return el.id || el.dataset.sectionId || '';
  }

  /**
   * 获取 Section 类型（基于 CSS 类名或 data 属性）
   * @param {Element} el
   * @returns {string}
   */
  function getSectionType(el) {
    const wrapper = el.closest('[id^="shopify-section-"]');
    if (!wrapper) return 'unknown';
    // Shopify section class: shopify-section + 自定义 section name
    const classList = Array.from(wrapper.classList);
    const sectionClass = classList.find(
      (c) => c !== 'shopify-section' && !c.startsWith('shopify-section--')
    );
    return sectionClass || 'section';
  }

  // ─── 全局状态 ────────────────────────────────────────────────────────────────

  const viewedSections = new Set();
  const scrollDepthReported = new Set();
  let sectionIndex = 0;

  // ─── Section View 追踪 ────────────────────────────────────────────────────────

  /**
   * 初始化 section_view 追踪
   * 使用 IntersectionObserver 监听所有 Shopify section
   */
  function initSectionViewTracking() {
    const sections = document.querySelectorAll('[id^="shopify-section-"]');
    if (!sections.length) return;

    // 建立 section 位置索引
    const sectionList = Array.from(sections);
    sectionList.forEach((el, idx) => {
      el.dataset.sectionIndex = idx;
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target;
          const sectionId = el.id.replace('shopify-section-', '');

          // 防重复：每个 section 只追踪一次曝光
          if (viewedSections.has(sectionId)) return;

          // 停留 800ms 后才算有效曝光
          const timer = setTimeout(() => {
            // 再次确认仍在视口内
            if (!viewedSections.has(sectionId)) {
              viewedSections.add(sectionId);
              const idx = parseInt(el.dataset.sectionIndex || '0', 10);

              publishEvent('section_view', {
                section_id: sectionId,
                section_type: getSectionType(el),
                section_index: idx,
                page_path: window.location.pathname,
              });

              // 同时触发滚动深度追踪
              trackScrollDepth(el, idx);
            }
          }, 800);

          // 如果 800ms 内离开视口则取消
          const leaveObserver = new IntersectionObserver(
            (leaveEntries) => {
              leaveEntries.forEach((leaveEntry) => {
                if (!leaveEntry.isIntersecting) {
                  clearTimeout(timer);
                  leaveObserver.disconnect();
                }
              });
            },
            { threshold: 0.5 }
          );
          leaveObserver.observe(el);
        });
      },
      {
        threshold: 0.5, // 50% 进入视口时触发
        rootMargin: '0px',
      }
    );

    sections.forEach((el) => observer.observe(el));
  }

  // ─── Scroll Depth 追踪 ────────────────────────────────────────────────────────

  /**
   * 追踪滚动深度（当 section 进入视口时记录百分比）
   * @param {Element} el
   * @param {number}  sectionIdx
   */
  function trackScrollDepth(el, sectionIdx) {
    const key = `${sectionIdx}`;
    if (scrollDepthReported.has(key)) return;
    scrollDepthReported.add(key);

    const pageHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      1
    );
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const viewportHeight = window.innerHeight || 0;
    const pct = Math.min(
      100,
      Math.round(((scrollTop + viewportHeight) / pageHeight) * 100)
    );

    publishEvent('scroll_depth', {
      section_index: sectionIdx,
      section_id: el.id.replace('shopify-section-', ''),
      pct: pct,
      page_path: window.location.pathname,
    });
  }

  // ─── Section Click 追踪 ───────────────────────────────────────────────────────

  /**
   * 初始化 section_click 追踪（事件委托，监听所有 section 内的点击）
   */
  function initSectionClickTracking() {
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target.closest('a, button');
        if (!target) return;

        // 只追踪 section 内的点击
        const sectionWrapper = target.closest('[id^="shopify-section-"]');
        if (!sectionWrapper) return;

        const sectionId = sectionWrapper.id.replace('shopify-section-', '');

        // 获取点击文本（用于 CTA 识别）
        const clickText = (
          target.textContent ||
          target.getAttribute('aria-label') ||
          ''
        )
          .trim()
          .slice(0, 100);

        const clickUrl =
          target.getAttribute('href') ||
          target.dataset.href ||
          '';

        // 排除纯交互按钮（购物车、导航切换等）
        const isNavControl =
          target.getAttribute('aria-controls') ||
          target.getAttribute('is') === 'previous-button' ||
          target.getAttribute('is') === 'next-button' ||
          target.getAttribute('is') === 'control-button';

        if (isNavControl) return;

        publishEvent('section_click', {
          section_id: sectionId,
          section_type: getSectionType(sectionWrapper),
          click_text: clickText,
          click_url: clickUrl,
          page_path: window.location.pathname,
        });
      },
      { passive: true }
    );
  }

  // ─── Expand (FAQ / 折叠) 追踪 ────────────────────────────────────────────────

  /**
   * 初始化 expand 追踪（监听所有 <details> 元素的展开事件）
   */
  function initExpandTracking() {
    // 使用事件委托监听 <summary> 点击
    document.addEventListener(
      'click',
      (event) => {
        const summary = event.target.closest('summary');
        if (!summary) return;

        const details = summary.closest('details');
        if (!details) return;

        // 只在展开时追踪（当前状态为关闭 → 点击后将展开）
        if (details.open) return;

        const sectionWrapper = details.closest('[id^="shopify-section-"]');
        if (!sectionWrapper) return;

        const sectionId = sectionWrapper.id.replace('shopify-section-', '');
        const expandTitle = (
          summary.textContent ||
          summary.querySelector('span')?.textContent ||
          ''
        )
          .trim()
          .replace(/\s+/g, ' ')
          .slice(0, 150);

        publishEvent('expand', {
          section_id: sectionId,
          expand_title: expandTitle,
          page_path: window.location.pathname,
        });
      },
      { passive: true }
    );
  }

  // ─── 初始化入口 ───────────────────────────────────────────────────────────────

  function init() {
    // 在主题编辑器中禁用追踪（避免污染数据）
    if (
      document.documentElement.classList.contains('shopify-design-mode') ||
      window.Shopify?.designMode
    ) {
      return;
    }

    initSectionViewTracking();
    initSectionClickTracking();
    initExpandTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // 支持 Shopify Theme Editor 的 section:load 事件（动态添加 section 时重新观察）
  document.addEventListener('shopify:section:load', () => {
    // 重新扫描新增的 sections
    const newSections = document.querySelectorAll(
      '[id^="shopify-section-"]:not([data-section-index])'
    );
    if (!newSections.length) return;

    const allSections = document.querySelectorAll('[id^="shopify-section-"]');
    allSections.forEach((el, idx) => {
      el.dataset.sectionIndex = idx;
    });
    // 重新初始化观察器将由 init() 的完整逻辑处理
  });
})();
